import { Badge } from '@web-app/components/ui/Badge/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@web-app/components/ui/Collapsible/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@web-app/components/ui/Tooltip/tooltip';
import type {
  PatientLabPanel,
  PatientLabResult,
  PatientLabTest,
} from '@web-app/server/services/lab-results/get-patient-labs.service';
import { LuChevronDown } from 'react-icons/lu';

type Props = {
  panels: PatientLabPanel[] | undefined;
  isLoading: boolean;
};

const CHEVRON =
  'transition-transform [[data-panel-open]_&]:rotate-180 text-muted-foreground';

/** Sources often send a date with no time, stored as UTC midnight, so the UTC calendar day is the clinical day. */
function formatClinicalDate(effectiveAt: string | null): string {
  const match = effectiveAt?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return 'Date unknown';
  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
}

function panelSummary(panel: PatientLabPanel): string {
  const latest = panel.tests
    .map((test) => test.results[0])
    .filter((result): result is PatientLabResult => Boolean(result));
  if (latest.length === 0) return 'No results';

  const newestDate = latest.reduce<string | null>(
    (newest, { effectiveAt }) =>
      effectiveAt && (!newest || effectiveAt > newest) ? effectiveAt : newest,
    null,
  );
  const flagged = latest.filter((result) => result.flag).length;
  const date = formatClinicalDate(newestDate);
  return flagged > 0 ? `${date} · ${flagged} flagged` : date;
}

function ResultLine({ result }: { result: PatientLabResult }) {
  return (
    <div className="flex items-start justify-between gap-4 text-sm">
      <div className="min-w-0">
        <Tooltip>
          <TooltipTrigger
            render={
              <span className="cursor-help font-medium text-foreground underline decoration-dotted underline-offset-2" />
            }
          >
            {result.value}
            {result.unit ? ` ${result.unit}` : ''}
          </TooltipTrigger>
          <TooltipContent>
            {result.labName ? `${result.labName} · ` : ''}LOINC {result.code}
          </TooltipContent>
        </Tooltip>
        {result.flag && (
          <Badge variant="warning" className="ml-2">
            {result.flag}
          </Badge>
        )}
        {result.referenceRange && (
          <p className="text-xs text-muted-foreground">
            Range {result.referenceRange}
          </p>
        )}
      </div>
      <span className="shrink-0 text-right text-muted-foreground">
        {formatClinicalDate(result.effectiveAt)} · {result.source}
      </span>
    </div>
  );
}

function LabTestRow({ test }: { test: PatientLabTest }) {
  const [latest, ...earlier] = test.results;

  return (
    <Collapsible className="py-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{test.name}</p>
        {earlier.length > 0 && (
          <CollapsibleTrigger className="flex items-center gap-1 rounded px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted/40">
            {earlier.length} earlier
            <LuChevronDown className={`size-3 ${CHEVRON}`} />
          </CollapsibleTrigger>
        )}
      </div>
      {latest ? (
        <ResultLine result={latest} />
      ) : (
        <p className="text-sm text-muted-foreground">No result</p>
      )}
      <CollapsibleContent className="mt-2 flex flex-col gap-2 border-l border-border pl-3">
        {earlier.map((result, index) => (
          <ResultLine
            key={`${result.effectiveAt}-${result.value}-${index}`}
            result={result}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

export function PatientLabResults({ panels, isLoading }: Props) {
  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (!panels) {
    return (
      <p className="text-sm text-destructive">
        Lab results could not be loaded. Close and try again.
      </p>
    );
  }

  return (
    <TooltipProvider delay={200}>
      <div className="flex flex-col divide-y divide-border/60">
        {panels.map((panel) => (
          <Collapsible key={panel.name} className="py-1">
            <CollapsibleTrigger className="flex w-full items-center justify-between py-2 text-left">
              <span className="text-sm font-semibold text-foreground">
                {panel.name}
              </span>
              <span className="flex items-center gap-2 text-xs text-muted-foreground">
                {panelSummary(panel)}
                <LuChevronDown className={`size-4 ${CHEVRON}`} />
              </span>
            </CollapsibleTrigger>
            <CollapsibleContent className="flex flex-col divide-y divide-border/60 pb-2">
              {panel.tests.map((test) => (
                <LabTestRow key={test.name} test={test} />
              ))}
            </CollapsibleContent>
          </Collapsible>
        ))}
      </div>
    </TooltipProvider>
  );
}
